import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { captureException } from '@sentry/gatsby';
import { useShowroomUrls } from '../hooks/use-showroom-urls';
import { getShowroomByIP, getMarketByIP } from '../actions/location-actions';
import { setLocalStorage, setCookie, getLocalStorage } from '../components/utilities/functions';
import { useUtilityFunction } from '../hooks/use-utility-functions';

const LocationContext = createContext({});

export const LocationProvider = ({ children }) => {
	const [routeChangeComplete, setRouteChangeComplete] = useState(false);
	const [branchInitialized, setBranchInitialized] = useState(false);
	const dispatch = useDispatch();
	const { updateLocation: utilityUpdateLocation } = useUtilityFunction();
	const { newShowrooms } = useShowroomUrls();
	const locations = useSelector((state) => state.locations);

	const [state, setState] = useState({
		userZip: '',
		selectedShowroom: null,
		loading: false,
		error: null,
		openLocation: false,
		changeLocation: false,
		locationList: [],
		geolocationZip: { enabled: true, saved: null },
		showrooms: [],
		manuallySelected: false,
	});

	const hasUpdatedLocation = useRef(false);

	useEffect(() => {
		if (getLocalStorage('UserLocation') == null && !locations.showroom) {
			dispatch(getShowroomByIP());
		}
	}, [locations.showroom, dispatch]);

	const updateState = (updates) => {
		setState((prev) => ({ ...prev, ...updates }));
	};

	const getShowroomHeading = useCallback(
		(storeNumber) => {
			return newShowrooms?.nodes?.find((s) => s.showroom_id === storeNumber)?.page_heading?.replace(/<\/?span[^>]*>/g, '');
		},
		[newShowrooms]
	);

	const getShowroomUrl = useCallback(
		(storeNumber) => {
			return newShowrooms?.nodes?.find((s) => s.showroom_id === storeNumber)?.url;
		},
		[newShowrooms]
	);

	const getShowroomData = useCallback(
		(store) => ({
			...store,
			heading: getShowroomHeading(store.store_number),
			url: getShowroomUrl(store.store_number),
		}),
		[getShowroomHeading, getShowroomUrl]
	);

	const saveLocationToStorage = useCallback((data) => {
		setLocalStorage('UserLocation', {
			postal_code: data.postal_code,
			city: data.city,
			state: data.state,
			branch_id: data.branch_id,
			store_number: data.store_number,
		});
		setCookie('LocationLoaded', true);
	}, []);

	const fetchShowroomsByZip = async (zip) => {
		try {
			updateState({ loading: true, error: null });
			const response = await axios.get(`${process.env.GATSBY_PELLA_API}/location/v2/showrooms/${zip}/`);

			if (response?.data?.showrooms?.[0]) {
				const enrichedShowrooms = response.data.showrooms.map(getShowroomData);
				updateState({
					locationList: enrichedShowrooms,
				});
			} else if (response.data?.error?.message) {
				const errorMsg = response.data.error.message.toLowerCase() === 'no showrooms are nearby' ? 'There are no showrooms within 200 miles of this location.' : response.data.error.message;

				updateState({
					error: errorMsg,
					locationList: [],
				});
			}
		} catch (err) {
			updateState({ error: 'Failed to fetch showrooms' });
		} finally {
			updateState({ loading: false });
		}
	};

	const updateLocation = useCallback(
		(store, zip) => {
			const showroomData = getShowroomData(store);

			updateState({
				selectedShowroom: showroomData,
				changeLocation: false,
				userZip: zip,
				manuallySelected: true,
			});

			saveLocationToStorage({
				postal_code: zip,
				city: store.city,
				state: store.state,
				branch_id: store.branch_id,
				store_number: store.store_number,
			});

			utilityUpdateLocation(zip);
		},
		[getShowroomData, saveLocationToStorage, utilityUpdateLocation]
	);

	const getCurrentLocation = async () => {
		if (!navigator.geolocation) {
			updateState({
				geolocationZip: { enabled: false, saved: null },
				error: 'Geolocation is not supported',
			});
			return;
		}

		updateState({ loading: true });

		navigator.geolocation.getCurrentPosition(async (position) => {
			try {
				const { latitude, longitude } = position.coords;

				dispatch(getMarketByIP([longitude, latitude]));
				dispatch(getShowroomByIP([longitude, latitude]));

				const response = await axios.get(`${process.env.GATSBY_PELLA_API}/location/v2/showroom`, {
					params: { lat: latitude, lon: longitude },
				});

				if (response?.data?.user_location?.postal_code) {
					const zip = response.data.user_location.postal_code;
					updateState({
						geolocationZip: { enabled: true, saved: zip },
						userZip: zip,
					});
					fetchShowroomsByZip(zip);
				} else {
					updateState({
						error: 'Unable to find your location',
						loading: false,
					});
				}
			} catch (err) {
				updateState({
					error: 'Unable to find your location',
					loading: false,
				});
			}
		});
	};

	useEffect(() => {
		if (!state.manuallySelected && !hasUpdatedLocation.current) {
			const showroom = locations?.showroom;
			const firstShowroom = locations?.showrooms?.[0];

			if (showroom?.store_number && !state.selectedShowroom?.store_number) {
				updateLocation(showroom, showroom.postal_code);
				hasUpdatedLocation.current = true;
			} else if (locations?.showrooms?.length && locations?.event !== 'LOCATION_SELECTED' && firstShowroom?.store_number !== state.selectedShowroom?.store_number) {
				updateLocation(firstShowroom, firstShowroom.postal_code);
				hasUpdatedLocation.current = true;
			}
		}
	}, [locations.showroom, locations.showrooms, locations.event, state.selectedShowroom?.store_number, state.manuallySelected, updateLocation]);

	const resetManualSelection = useCallback(() => {
		updateState({ manuallySelected: false });
	}, []);

	useEffect(() => {
		if (routeChangeComplete) {
			const timer = setTimeout(() => {
				setRouteChangeComplete(false);
			}, 300);
			return () => clearTimeout(timer);
		}
	}, [routeChangeComplete]);

	const setLocation = useCallback(
		async (zip, cart = true, event = null) => {
			try {
				updateState({ loading: true, error: null });

				await utilityUpdateLocation(zip, cart, event);

				const response = await axios.get(`${process.env.GATSBY_PELLA_API}/location/v2/showrooms/${zip}`);
				const showrooms = response.data?.showrooms || [];

				if (showrooms.length > 0) {
					const firstShowroom = showrooms[0];
					updateLocation(firstShowroom, zip);
				} else {
					updateState({
						error: 'No showrooms found for the provided zip code.',
					});
				}
			} catch (error) {
				updateState({
					error: 'Failed to initialize location. Please try again.',
				});
				captureException(error);
			} finally {
				updateState({ loading: false });
			}
		},
		[utilityUpdateLocation, updateLocation]
	);

	const value = {
		...state,
		routeChangeComplete,
		setRouteChangeComplete,
		branchInitialized,
		setBranchInitialized,
		updateState,
		updateLocation,
		fetchShowroomsByZip,
		getCurrentLocation,
		getShowroomData,
		newShowrooms,
		resetManualSelection,
		setLocation,
	};

	return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
};

LocationProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export const useLocation = () => {
	const context = useContext(LocationContext);
	return context || {};
};

export default LocationProvider;
